import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import DOMPurify from 'dompurify';

interface EmailTemplate {
  id: string;
  template_name: string;
  subject: string;
  body_html: string;
  body_text: string;
  variables: string[];
  is_active: boolean;
  category: string;
}

interface EmailPreviewModalProps {
  template: EmailTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Sample data for preview
const sampleData: Record<string, string> = {
  client_name: 'John Doe',
  login_url: 'https://example.com/portal',
  support_email: 'support@tinytaxlady.com',
  document_count: '3',
  upload_date: 'January 15, 2025',
  portal_url: 'https://example.com/portal',
  service_type: 'Tax Consultation',
  preferred_date: 'January 20, 2025',
  preferred_time: '2:00 PM',
  appointment_date: 'January 20, 2025',
  appointment_time: '2:00 PM',
  duration: '60',
  meeting_link: 'https://meet.example.com/abc123',
  tax_year: '2024',
  filing_date: 'January 25, 2025',
  filing_status: 'Single',
};

function replaceVariables(text: string): string {
  let result = text;
  Object.entries(sampleData).forEach(([key, value]) => {
    result = result.replace(new RegExp(`{{${key}}}`, 'g'), value);
  });
  return result;
}

export default function EmailPreviewModal({
  template,
  open,
  onOpenChange,
}: EmailPreviewModalProps) {
  const previewSubject = replaceVariables(template.subject);
  const previewHtml = replaceVariables(template.body_html);
  const previewText = replaceVariables(template.body_text);

  // Sanitize HTML to prevent XSS
  const sanitizedHtml = DOMPurify.sanitize(previewHtml, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'img', 'div', 'span', 'table', 'thead', 'tbody', 'tr', 'td', 'th'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class', 'style']
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Email Preview</DialogTitle>
          <DialogDescription>
            Preview how this email will look with sample data
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border p-4 bg-muted/50">
            <p className="text-sm font-medium mb-2">Subject:</p>
            <p className="text-sm">{previewSubject}</p>
          </div>

          <Tabs defaultValue="html">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="html">HTML Preview</TabsTrigger>
              <TabsTrigger value="text">Plain Text</TabsTrigger>
            </TabsList>

            <TabsContent value="html">
              <div className="rounded-lg border p-6 bg-background">
                <div
                  dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                  className="prose prose-sm max-w-none"
                />
              </div>
            </TabsContent>

            <TabsContent value="text">
              <div className="rounded-lg border p-6 bg-background">
                <pre className="whitespace-pre-wrap text-sm font-sans">
                  {previewText}
                </pre>
              </div>
            </TabsContent>
          </Tabs>

          <div className="rounded-lg border p-4 bg-muted/50">
            <p className="text-xs text-muted-foreground">
              Note: This is a preview with sample data. Actual emails will use real client information.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
